import type { SpecificNeedsConstraints } from "@/types/needsContrains";
import type {
    NeedsConstraintsExceptionsUpdateModel,
    SpecificNeedsConstraintsUpdateModel,
} from "@/types/needsContrainsRequest";
import type { HolidayInclusion } from "@/types";
import type { RoleInfo } from "./roleInterfaces";

export const DAY_DEFAULT: SpecificNeedsConstraints = Object.freeze({
    active: "true",
    apply: "true",
    min: 0,
    max: 0,
    start: 0,
    end: 0,
});

const EDITABLE_FIELDS = ["min", "max", "start", "end"] as const;

export const isApplied = (config: SpecificNeedsConstraints): boolean =>
    config.active !== "false" && config.apply !== "false";

export function getDayOfWeekDefault(role: RoleInfo, dayOfWeek: number): SpecificNeedsConstraints {
    return role.dicOfDaysOfWeek?.[String(dayOfWeek)] ?? DAY_DEFAULT;
}

export function getEffectiveConfig(role: RoleInfo, dateStr: string): SpecificNeedsConstraints {
    const specDay = role.dicOfSpecificDays?.[dateStr];

    if (specDay && specDay.active !== "false") {
        return specDay;
    }

    const date = new Date(dateStr + "T00:00:00");
    return getDayOfWeekDefault(role, date.getDay());
}

export function equalsDefaultOnEditable(
    config: SpecificNeedsConstraints,
    dayDefault: SpecificNeedsConstraints
): boolean {
    return isApplied(config) === isApplied(dayDefault)
        && EDITABLE_FIELDS.every((f) => (config[f] ?? 0) === (dayDefault[f] ?? 0));
}

function buildHolidayExceptionPayload(
    holiday: HolidayInclusion | undefined
): NeedsConstraintsExceptionsUpdateModel {
    const skipIfMatch = holiday === "Exclure";
    const skipIfNoMatch = holiday === "Seulement";
    const unset = !(skipIfMatch || skipIfNoMatch);

    return {
        holidayUnset: unset,
        holiday: {
            activeUnset: unset ? undefined : true,
            active: unset ? "false" : undefined,
            actionIfMatchUnset: skipIfMatch ? undefined : true,
            actionIfMatch: skipIfMatch ? "skip" : undefined,
            actionIfNoMatchUnset: skipIfNoMatch ? undefined : true,
            actionIfNoMatch: skipIfNoMatch ? "skip" : undefined,
        },
    };
}

export function buildSpecDaysPayload(
    role: RoleInfo,
    draft: Record<string, SpecificNeedsConstraints>
): Record<string, SpecificNeedsConstraintsUpdateModel> {
    const result: Record<string, SpecificNeedsConstraintsUpdateModel> = {};
    // A dedicated need key carries the holiday semantic itself; sending the exception block
    // would unset flags that may still exist on the stored data.
    const holidayException = role.needId ? undefined : buildHolidayExceptionPayload(role.holiday);

    for (const [dateStr, draftEntry] of Object.entries(draft)) {
        const date = new Date(dateStr + "T00:00:00");
        const dayDefault = getDayOfWeekDefault(role, date.getDay());
        const preExisting = role.dicOfSpecificDays?.[dateStr];
        const hasActivePreExisting = !!preExisting && preExisting.active !== "false";

        if (!equalsDefaultOnEditable(draftEntry, dayDefault)) {
            result[dateStr] = {
                activeUnset: true,
                active: "true",
                apply: isApplied(draftEntry) ? "true" : "false",
                min: draftEntry.min ?? 0,
                max: draftEntry.max ?? 0,
                start: draftEntry.start ?? 0,
                end: draftEntry.end ?? 0,
                equity: draftEntry.equity,
                exceptions: holidayException,
            };
        } else if (hasActivePreExisting) {
            result[dateStr] = { active: "false" };
        }
    }

    return result;
}
